// Type definitions for NetSuite CRX filters
interface NetSuiteFilterOption {
  text: string;
  value: string;
}

interface NetSuiteTimeSelectorOption {
  favourite: boolean;
  tagsCode: number;
  text: string;
  value: string;
}

export interface NetSuiteMultiSelectFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "select";
  options: NetSuiteFilterOption[];
  value: string | string[];
  multiple: boolean;
  checkAll: boolean;
}

export interface NetSuiteTimeSelectorFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "timeselector";
  options: NetSuiteTimeSelectorOption[];
  value: string;
  multiple: boolean;
}

export interface NetSuiteDateFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "date";
  value: string;
}

export interface NetSuiteTextFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "text";
  value: string;
}

export interface NetSuiteCurrencyFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "currency";
  value: string;
}

export interface NetSuiteCheckboxFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "checkbox";
  value: boolean;
}

export interface NetSuiteReadOnlyTextFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "readonlyText";
}

export interface NetSuiteIntegerFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "integer";
  value: string;
}

export interface NetSuiteClobtextFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "clobtext";
  value: string;
}
export interface NetSuiteFloatFilter {
  fieldLabel: string;
  fieldName: string;
  fieldType: "float";
  value: string;
}

export type NetSuiteFilter =
  | NetSuiteMultiSelectFilter
  | NetSuiteTimeSelectorFilter
  | NetSuiteDateFilter
  | NetSuiteTextFilter
  | NetSuiteCurrencyFilter
  | NetSuiteCheckboxFilter
  | NetSuiteReadOnlyTextFilter
  | NetSuiteIntegerFilter
  | NetSuiteClobtextFilter
  | NetSuiteFloatFilter;

export interface NetSuiteFilterResponse {
  filters?: NetSuiteFilter[] | null;
  errors?: string[] | null;
  screenshot?: string;
}

export type PostMessageNetsuiteFilterResponse = {
  action: string;
  success: boolean;
  filters?: NetSuiteFilter[] | null;
  errors?: string[] | null;
  debug?: string[] | null;
  requestId?: string;
};

export const isNetSuiteFilterSupported = (url: string): boolean => {
  try {
    const urlObj = new URL(url);

    const pathSegments = urlObj.pathname.split("/");
    const reportType = pathSegments[pathSegments.length - 1].replace(".nl", "");

    return !!(reportType === "reportrunner");
  } catch (error) {
    return false;
  }
};

// Helper function to check if a filter is a parent "mod" filter
const isModFilter = (fieldName: string): boolean => {
  return fieldName.endsWith("_mod");
};

const getModePrefix = (fieldName: string): string => {
  return fieldName.split("_mod")[0];
};

const getModFilterValue = (filter: NetSuiteTimeSelectorFilter): string => {
  if (!filter) {
    return "";
  }

  let value = filter.value;

  if (filter.options) {
    const option = filter.options.find(option => option.value === value || option.text === value);
    value = option ? option.value : value;
  }

  return value;
};

export const getVisibleNetSuiteFilters = (filters: NetSuiteFilter[]): string[] => {
  const modfilters = filters.filter(filter => isModFilter(filter.fieldName));
  const modPrefixes = modfilters.map(filter => getModePrefix(filter.fieldName));
  const visibleFilters = new Set<string>(filters.map(filter => filter.fieldName));

  for (const modPrefix of modPrefixes) {
    const dependentFilters = filters.filter(
      filter =>
        getModePrefix(filter.fieldName).startsWith(`${modPrefix}_`) &&
        !isModFilter(filter.fieldName)
    );

    const modFilter = modfilters.find(filter => filter.fieldName === `${modPrefix}_mod`);

    for (const dependentFilter of dependentFilters) {
      // Type guard to check if modFilter has a value property
      const hasValue = modFilter && "value" in modFilter;
      const modeFilterValue = getModFilterValue(modFilter as NetSuiteTimeSelectorFilter);
      if (hasValue && !["CUSTOM", "(Custom)"].includes(modeFilterValue)) {
        visibleFilters.delete(dependentFilter.fieldName);
      } else {
        visibleFilters.add(dependentFilter.fieldName);
      }
    }
  }

  return Array.from(visibleFilters);
};

export const ingoreHiddenFilters = (filters: NetSuiteFilter[]): NetSuiteFilter[] => {
  const visibleFilters = getVisibleNetSuiteFilters(filters);
  return filters.filter(filter => visibleFilters.includes(filter.fieldName));
};
